import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { Project, Revision } from '../../../../shared/model-typings';

/** Exported so that `useCurrentRevision` can compose its own `useQuery` call with custom options (polling, etc.) while reusing the same fetch logic and query cache. */
export function getMinimalRevision(revisionLocator: string) {
  return get<Revision & { project: Project }>(`/api/revisions/${nuc(revisionLocator)}/minimal`, true);
}

export const getMinimalQueryKey = (revisionLocator: string): QueryKey => ['revisions', revisionLocator, 'minimal'];

/**
 * Fetch a single revision by its locator using the minimal endpoint.
 *
 * @param {string} revisionLocator - The locator of the revision to fetch. No need to call nuc before passing it in here.
 * @param {boolean} shouldFetch - Allows you to delay the fetching of the revision. This can be useful if (for example) the full revision locator isn't available yet.
 */
export default function useRevisionMinimal(
  revisionLocator: string,
  shouldFetch: boolean = true,
) {
  return useQuery({
    queryKey: getMinimalQueryKey(revisionLocator),
    queryFn: () => getMinimalRevision(revisionLocator),
    enabled: shouldFetch,
  });
}
